import { Helmet } from 'react-helmet-async';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  url?: string;
  type?: string;
  streamsCount?: number;
  viewersCount?: number;
}

const SEO: React.FC<SEOProps> = ({
  title = 'ArkaneTV - Streams en direct des joueurs Arkane',
  description = 'Découvrez les streams en direct des joueurs de la communauté Arkane. Regardez vos streamers préférés sur Twitch et Kick en temps réel avec notre multiview innovant.',
  keywords = 'Arkane, ArkaneTV, streaming, Twitch, Kick, FiveM, GTA RP, roleplay, streams en direct, multiview, gaming, communauté',
  image = 'https://cdn.arkane-worldwide.com/assets/logo/logo_midres_wbg.png',
  url = 'https://tv.arkane-worldwide.com',
  type = 'website',
  streamsCount,
  viewersCount
}) => {
  const currentUrl = url;
  const fullTitle = title.includes('ArkaneTV') ? title : `${title} | ArkaneTV`;
  
  // Dynamic description based on live data
  const dynamicDescription = streamsCount && viewersCount 
    ? `${description} Actuellement ${streamsCount} streamer${streamsCount > 1 ? 's' : ''} en ligne avec ${viewersCount} viewers au total.`
    : description;

  const structuredData: any = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "name": "ArkaneTV",
    "description": dynamicDescription,
    "url": currentUrl,
    "logo": image,
    "applicationCategory": "Entertainment",
    "operatingSystem": "Web Browser",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "EUR"
    },
    "creator": {
      "@type": "Organization",
      "name": "Arkane Worldwide",
      "url": "https://arkane-worldwide.com",
      "logo": image,
      "sameAs": [
        "https://www.youtube.com/@arkaneworldwide",
        "https://twitter.com/ArkaneWW",
        "https://discord.gg/arknwl",
        "https://www.tiktok.com/@arkane.ww"
      ]
    },
    "publisher": {
      "@type": "Organization",
      "name": "Arkane Worldwide",
      "logo": {
        "@type": "ImageObject",
        "url": image
      }
    }
  };

  // Add live streaming data if available
  if (streamsCount && streamsCount > 0) {
    structuredData.potentialAction = {
      "@type": "WatchAction",
      "target": currentUrl,
      "actionStatus": "ActiveActionStatus"
    };
  }

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={dynamicDescription} />
      <meta name="keywords" content={keywords} />
      <meta name="author" content="Arkane Worldwide" />
      <meta name="robots" content="index, follow, max-image-preview:large" />
      <meta name="googlebot" content="index, follow" />
      
      {/* Language and Region */}
      <html lang="fr" />
      <meta name="language" content="French" />
      <meta name="geo.region" content="FR" />
      <meta name="geo.placename" content="France" />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={dynamicDescription} />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:url" content={currentUrl} />
      <meta property="og:site_name" content="ArkaneTV" />
      <meta property="og:locale" content="fr_FR" />
      
      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@ArkaneWW" />
      <meta name="twitter:creator" content="@ArkaneWW" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={dynamicDescription} />
      <meta name="twitter:image" content={image} />
      
      {/* Additional Meta Tags */}
      <meta name="theme-color" content="#37633f" />
      <meta name="msapplication-TileColor" content="#37633f" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="black-translucent" />
      <meta name="apple-mobile-web-app-title" content="ArkaneTV" />
      
      {/* Canonical URL */}
      <link rel="canonical" href={currentUrl} />
      
      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(structuredData, null, 2)}
      </script>
      
      {/* Additional structured data for live streams */}
      {streamsCount && streamsCount > 0 && (
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LiveBlogPosting",
            "headline": `${streamsCount} streams en direct sur ArkaneTV`,
            "description": `Regardez ${streamsCount} streamer${streamsCount > 1 ? 's' : ''} de la communauté Arkane en direct`,
            "datePublished": new Date().toISOString(),
            "dateModified": new Date().toISOString(),
            "author": {
              "@type": "Organization",
              "name": "Arkane Worldwide"
            },
            "publisher": {
              "@type": "Organization",
              "name": "Arkane Worldwide",
              "logo": {
                "@type": "ImageObject",
                "url": image
              }
            },
            "mainEntityOfPage": {
              "@type": "WebPage",
              "@id": currentUrl
            }
          }, null, 2)}
        </script>
      )}
      
      {/* Performance and Caching */}
      <meta httpEquiv="Cache-Control" content="public, max-age=31536000" />
      <meta name="format-detection" content="telephone=no" />
      
      {/* Favicon and Icons */}
      <link rel="icon" type="image/png" sizes="32x32" href={image} />
      <link rel="icon" type="image/png" sizes="16x16" href={image} />
      <link rel="apple-touch-icon" href={image} />
      
      {/* Preconnect for performance */}
      <link rel="preconnect" href="https://embed.twitch.tv" />
      <link rel="preconnect" href="https://player.kick.com" />
      <link rel="preconnect" href="https://cdn.arkane-worldwide.com" />
      <link rel="dns-prefetch" href="https://www.twitch.tv" />
      <link rel="dns-prefetch" href="https://kick.com" />
      
      {/* Manifest for PWA */}
      <link rel="manifest" href="/manifest.json" />
    </Helmet>
  );
};

export default SEO;